import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../AppContext'; 
import { getDatabase, ref, get, onValue, off } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';

const WalletPage = () => {
  const {
    datWallet,
    assetsSummary,
    currentUser,
    userWallet,
    userAssets: ctxUserAssets,
    userId2,
    datClaimableUsd,
    datClaimableTokens,
    datTokenSpotUsd,
    datRewardWalletQty,
  } = useContext(AppContext);

  const [poolCents, setPoolCents] = useState(0);
  const [circulating, setCirculating] = useState(0);

  const navigate = useNavigate();

  const [walletSettingsVisible, setWalletSettingsVisible] = useState(false);
  const [earningsByAsset, setEarningsByAsset] = useState({});
  const [checkingStripeAccount, setCheckingStripeAccount] = useState(false);

  const [earnedCreditsCount, setEarnedCreditsCount] = useState(0);

  const toNum = (n) => {
    if (n == null) return 0;
    if (typeof n === 'number') return Number.isFinite(n) ? n : 0;
    if (typeof n === 'string') return Number(n) || 0;
    if (typeof n === 'object') return toNum(Object.values(n)[0]);
    return 0;
  };

  const blocks = useMemo(() => {
    if (!datWallet) return [];
    return Object.entries(datWallet)
      .map(([id, v]) => {
        const price = toNum(v?.price);
        const qty = toNum(v?.qty);
        return { id, price, qty };
      })
      .filter((b) => b.qty > 0)
      .sort((a, b) => Number(a.id) - Number(b.id));
  }, [datWallet]);

  const totals = useMemo(() => {
    return blocks.reduce(
      (acc, b) => {
        acc.tokens += b.qty;
        acc.utilityUsd += b.qty * b.price;
        return acc;
      },
      { tokens: 0, utilityUsd: 0 }
    );
  }, [blocks]);

  const effectiveUserAssets = useMemo(() => {
    if (ctxUserAssets?.length) return ctxUserAssets;
    if (!userWallet || !assetsSummary) return [];
    const owned = (userWallet.ownedDats || []).filter(
      (od) => typeof od.edition === 'number' && od.edition >= 1 && od.edition <= 20
    );
    const mapped =
      assetsSummary
        ?.map((asset) => {
          const ownedEditions = owned
            .filter((od) => od.id === asset.id)
            .map((od) => {
              const editionDetails = asset.editions.find((edition) => edition.edition === od.edition);
              return { ...editionDetails, id: asset.id, ...asset };
            });

          return ownedEditions.length ? { ...asset, ownedEditions } : null;
        })
        .filter(Boolean) || [];
    return mapped;
  }, [ctxUserAssets, userWallet, assetsSummary]);

  useEffect(() => {
    const db = getDatabase();
    let cancelled = false;

    (async () => {
      const tasks = (effectiveUserAssets || []).map(async (asset) => {
        const streamsRef = ref(db, `earnings/${asset.id}/totalEarnings`);
        const salesRef = ref(db, `earnings/${asset.id}/sales/totalEarnings`);
        const totalStrm = ref(db, `streamStats/${asset.id}/totalEarnings`);
        try {
          const [stSnap, saSnap, tsSnap] = await Promise.all([get(streamsRef), get(salesRef), get(totalStrm)]);
          const streams = stSnap.exists() ? Number(stSnap.val()) : 0;
          const sales = saSnap.exists() ? Number(saSnap.val()) : 0;
          const totalStreams = tsSnap.exists() ? Number(tsSnap.val()) : 0;

          const totalCalc = streams * 0.05 + sales;
return { id: asset.id, data: { streams, sales, totalStreams, totalCalc } };

        } catch (e) {
          console.error('earnings fetch err', e);
          return { id: asset.id, data: { streams: 0, sales: 0, totalStreams: 0, totalCalc: 0 } };
        }
      });

      const results = await Promise.all(tasks);
      if (cancelled) return;

      const next = {};
      results.forEach(({ id, data }) => {
        next[id] = data;
      });
      setEarningsByAsset(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveUserAssets]);

  useEffect(() => {
    const db = getDatabase();
    const poolRef = ref(db, 'metrics/datToken/poolCents');
    const circRef = ref(db, 'metrics/datToken/circulating');

const unsubPool = onValue(poolRef, (s) => setPoolCents(Number(s.val() || 0)), () => setPoolCents(0));
const unsubCirc = onValue(circRef, (s) => setCirculating(Number(s.val() || 0)), () => setCirculating(0));

return () => {
  unsubPool(); 
  unsubCirc();
};

  }, []);

  useEffect(() => {
    if (!userId2) return;
    const db = getDatabase();

    const candidates = [
      `earnedCreditsStrings/${userId2}`,
      `users/${userId2}/earnedCreditsStrings`,
      `credits/${userId2}/strings`,
      `wallets/${userId2}/earnedCreditsStrings`,
    ];

    let activeRef = null;
    let unsub = null;
    let cancelled = false;

    (async () => {
      for (const path of candidates) {
        try {
          const r = ref(db, path);
          const s = await get(r);
          if (cancelled) return;
          if (s.exists()) {
            activeRef = r;
            unsub = onValue(
              activeRef,
              (snap) => {
                const raw = snap.val();
                const n = typeof raw === 'object' ? Number(Object.values(raw)[0]) : Number(raw);
                setEarnedCreditsCount(Number.isFinite(n) ? n : 0);
              },
              () => setEarnedCreditsCount(0)
            );
            break;
          }
        } catch {
          /* try next */
        }
      }
    })();

    return () => {
      cancelled = true;
      if (activeRef && unsub) off(activeRef, 'value', unsub);
    };
  }, [userId2]);

  const toMoneyNumber = (v) => {
    if (v == null) return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const n = Number(String(v).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const computedSpotUsd = useMemo(() => {
    const c = Number(circulating) || 0;
    const p = Number(poolCents) || 0;
    if (!c) return 0;
    return p / 100 / c;
  }, [poolCents, circulating]);

  const valuePerDatTokenUsd = Number(datTokenSpotUsd) > 0 ? Number(datTokenSpotUsd) : computedSpotUsd;

  const myDatTokenQty = Number(datRewardWalletQty) || 0;
  const myDatTokenValueUsd = useMemo(() => {
    return myDatTokenQty * valuePerDatTokenUsd;
  }, [myDatTokenQty, valuePerDatTokenUsd]);

  const datEarningsHeader = useMemo(() => {
    if (userWallet?.ownedDats && userWallet.ownedDats.length) {
      const ownedIds = new Set((effectiveUserAssets || []).map((a) => a.id));
      const sumWallet = userWallet.ownedDats
        .filter((d) => ownedIds.has(d.id))
        .reduce((s, d) => s + toMoneyNumber(d.earnings), 0);
      if (sumWallet > 0) return sumWallet;
    }
    return 0;
  }, [userWallet, effectiveUserAssets, earningsByAsset]);

  const openCashoutSettings = async () => {
    setCheckingStripeAccount(true);
    try {
      const fn = httpsCallable(getFunctions(), 'createOrRetrieveAccount');
      const res = await fn({ email: currentUser?.email });
      if (!res?.data?.stripeAccountId || !res?.data?.payouts_enabled) {
        setWalletSettingsVisible(false);
        navigate('/cashout');
        return;
      }
      const linkFn = httpsCallable(getFunctions(), 'createStripeOnboardingLinkIOS');
      const link = await linkFn();
      window.open(link?.data?.url, '_blank');
      setWalletSettingsVisible(false);
    } catch (e) {
      console.error(e);
      alert('Unable to open Stripe – try again.');
    } finally {
      setCheckingStripeAccount(false);
    }
  };

  const openBillingSettings = () => {
    window.open('https://billing.stripe.com/p/login/14A8wO77gc5jgF58lHbsc00', '_blank');
    setWalletSettingsVisible(false);
  };

  const gridItems = useMemo(() => {
    const blockItems = blocks.map((b) => ({ kind: 'block', key: `block-${b.id}`, data: b }));
    const datItems = (effectiveUserAssets || []).map((a) => ({ kind: 'dat', key: `dat-${a.id}`, data: a }));
    return [...blockItems, ...datItems];
  }, [blocks, effectiveUserAssets]);

  const fmtUSD = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt3 = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });

  const royaltyPctForViewer = (asset) => {
    if (!asset?.ownedEditions || !userWallet?.ownedDats) return 0;
    const unsoldPct = asset.ownedEditions
      .filter((ed) => {
        const w = userWallet.ownedDats.find((e) => e.edition === ed.edition);
        return w && !w.sold;
      })
      .reduce((sum, ed) => sum + (ed.royalties || 0), 0);
    return userId2 === asset.creatorId ? unsoldPct + 50 : unsoldPct;
  };

  const walletEarningsForDat = (assetId) => {
    return (userWallet?.ownedDats || [])
      .filter((e) => e.id === assetId)
      .reduce((sum, e) => sum + toMoneyNumber(e.earnings), 0);
  };

  const CREDIT_USD_PER_STRING = 0.1;
  const earnedCreditsUsd = useMemo(() => {
    return (Number(earnedCreditsCount) || 0) * CREDIT_USD_PER_STRING;
  }, [earnedCreditsCount]);

  const earningsTotalUsd = (Number(datEarningsHeader) || 0) + earnedCreditsUsd + (Number(myDatTokenValueUsd) || 0);

  return (
    <div className="sf-wallet">
      <div className="sf-totals">
        <h2 className="sf-title">Wallet</h2>

        <div className="sf-cards">
          <div className="sf-card">
            <span className="sf-label">{(totals.utilityUsd)} UTILITY TOKENS</span>
            <span className="sf-value">${fmtUSD(totals.utilityUsd)}</span>
          </div>

          <div className="sf-card">
            <span className="sf-label">DATCHAIN TOKEN REWARDS</span>
            <span className="sf-value">{Number.parseInt(String(datRewardWalletQty ?? 0), 10).toLocaleString()}</span>
          </div>

          <div className="sf-card">
            <span className="sf-label">EARNINGS</span>
            <span className="sf-value">${fmt3(earningsTotalUsd)}</span>
          </div>

          <div className="sf-card">
            <span className="sf-label">VALUE PER DATCHAIN TOKEN</span>
            <span className="sf-value">${fmt3(valuePerDatTokenUsd)}</span>
          </div>
        </div>

        <div className="sf-actions">
          <button className="sf-btn" onClick={() => navigate('/cashout')}>Cash Out</button>
          <button className="sf-btn ghost" onClick={() => setWalletSettingsVisible(true)}>Wallet Settings</button>
        </div>
      </div>

      <div className="sf-grid">
        {gridItems.length ? (
          <ul className="sf-list">
            {gridItems.map((item) => {
  if (item.kind === 'block') {
    return null;
  }

              const a = item.data;
              const e = earningsByAsset[a.id] || { totalStreams: 0 };
              const displayPct = royaltyPctForViewer(a);
              const walletEarn = walletEarningsForDat(a.id);

              return (
                <li
                  key={item.key}
                  className="sf-dat"
                  onClick={() => navigate(`/wallet/dat/${a.id}`, { state: { dat: a } })}
                >
                  <img className="sf-art" src={a.ArtworkURL} alt={`Artwork for ${a.creatorUsername}`} />
                  <div className="sf-info">
                    <div className="sf-song">‘{a.SongName}’</div>
                    <div className="sf-artist">{a.ArtistName}</div>
                    <div className="sf-user">@{a.creatorUsername}</div>

                    <div className="sf-extra">Royalty Share: {displayPct.toFixed(2)}%</div>
                    <div className="sf-extra">Total Streams: {e.totalStreams.toLocaleString()}</div>
                    <div className="sf-extra">Earnings: ${fmtUSD(walletEarn)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="sf-empty">You don’t own any Utility Tokens or DATS yet.</p>
        )}
      </div>

      {walletSettingsVisible && (
        <div className="sf-overlay" onClick={() => setWalletSettingsVisible(false)}>
          <div className="sf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sf-modal-title">Wallet Settings</div>

            <div className="sf-modal-body">
              {checkingStripeAccount ? (
                <div className="sf-row">
                  <div className="sf-spinner" />
                  <div className="sf-note">Checking Stripe account…</div>
                </div>
              ) : (
                <>
                  <button className="sf-btn full" onClick={openCashoutSettings}>Edit Cash-out Details</button>
                  <button className="sf-btn full ghost" onClick={openBillingSettings}>Edit Billing Info</button>
                </>
              )}
            </div>

            <div className="sf-modal-actions">
              <button className="sf-btn ghost" onClick={() => setWalletSettingsVisible(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
:root{
  --sf-gold:#fdca05;
  --sf-gold-20:#fdca05;
  --sf-gold-08:rgba(245,198,93,.08);
  --sf-gold-15:rgba(245,198,93,.15);
  --sf-gold-25:rgba(245,198,93,.25);
  --sf-card-bg:rgba(255,255,255,.04);
  --sf-card-bd:rgba(255,255,255,.08);
}

*{box-sizing:border-box;}
html,body{background:#000;color:#fff;}
.sf-wallet{padding:24px 16px;max-width:1200px;margin:0 auto;font-family:'CustomFont',sans-serif;}

.sf-title{
  font-size:1.9rem;
  font-weight:800;
  text-shadow:0 0 18px rgba(245,198,93,.08);
  margin-bottom:.6rem;
  text-align:left;
}

.sf-cards{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(230px,1fr));
  gap:12px;
}
.sf-card{
  background:var(--sf-card-bg);
  border:1px solid var(--sf-card-bd);
  border-radius:12px;
  padding:.9rem 1rem;
}
.sf-label{
  display:block;
  color:rgba(255,255,255,.7);
  font-size:.85rem;
  letter-spacing:.02em;
}
.sf-value{
  display:block;
  font-size:1.35rem;
  font-weight:800;
  color:#fdca05;
  margin-top:.15rem;
}

.sf-actions{
  margin-top:.9rem;
  display:flex;
  gap:.6rem;
  flex-wrap:wrap;
}
  .social-links a {
  color: #fdca05;
  text-decoration: none;
}
.sf-btn{
  padding:.6rem 1.1rem;
  border-radius:10px;
  font-weight:800;
  cursor:pointer;
  background:#fdca05;
  color:#000;
  border:1px solid #fdca05;
  transition:transform .15s ease, box-shadow .15s ease, opacity .15s ease;
  box-shadow:0 4px 12px rgba(245,198,93,.22);
}
.sf-btn:hover{ transform:translateY(-1px); box-shadow:0 6px 16px rgba(245,198,93,.32); }
.sf-btn:active{ transform:translateY(0); }
.sf-btn.ghost{
  background:transparent;
  color:#fdca05;
  border:1px solid #fdca05;
  box-shadow:none;
}
.sf-btn.full{ width:100%; }

.sf-grid{ margin-top:18px; }
.sf-list{
  list-style:none;
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(260px,1fr));
  gap:12px;
}

/* Block card */
.sf-block{
  background:#0b0b0b;
  border:1px solid var(--sf-gold-15);
  border-radius:10px;
  padding:14px;
  transition:transform .18s ease, box-shadow .18s ease;
}
.sf-block:hover{ transform:translateY(-3px); box-shadow:0 6px 14px rgba(245,198,93,.12); }
.sf-block-id{
  font-weight:800;
  color:var(--sf-gold);
  margin-bottom:.4rem;
}
.sf-block-lines{ display:flex; flex-direction:column; gap:.35rem; }
.sf-line{ display:flex; justify-content:space-between; }
.sf-k{ color:rgba(255,255,255,.72); }
.sf-v{ color:#fff; font-weight:700; }

/* DAT card */
.sf-dat{
  display:flex;
  align-items:center;
  background:#0b0b0b;
  border:1px solid var(--sf-gold-15);
  border-radius:10px;
  padding:12px;
  gap:12px;
  transition:transform .2s ease, box-shadow .2s ease;
  cursor:pointer;
}
.sf-dat:hover{ transform:translateY(-4px); box-shadow:0 8px 18px rgba(245,198,93,.12); }
.sf-art{
  width:52px; height:52px; object-fit:cover; border-radius:8px;
  border:2px solid var(--sf-gold);
}
.sf-info{ display:flex; flex-direction:column; min-width:0; }
.sf-info > div{ white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sf-song{ font-weight:800; color:var(--sf-gold); }
.sf-artist{ color:#fff; opacity:.9; }
.sf-user{ color:#fff; opacity:.6; }
.sf-extra{ color:#fff; opacity:.75; font-size:.88rem; }

.sf-empty{ color:rgba(255,255,255,.65); }

/* Modal */
.sf-overlay{ position:fixed; inset:0; background:rgba(0,0,0,.65); display:flex; align-items:center; justify-content:center; z-index:999; }
.sf-modal{
  width:92%; max-width:460px; background:#0b0b0b;
  border:1px solid var(--sf-gold-15); border-radius:14px;
  padding:16px 16px 14px; box-shadow:0 10px 30px rgba(0,0,0,.45);
}
.sf-modal-title{ font-size:1.05rem; font-weight:800; color:var(--sf-gold); margin-bottom:.6rem; }
.sf-modal-body{ display:flex; flex-direction:column; gap:.6rem; margin:.5rem 0 1rem; }
.sf-modal-actions{ display:flex; justify-content:flex-end; gap:.5rem; }
.sf-row{ display:flex; align-items:center; gap:.75rem; }
.sf-note{ color:rgba(255,255,255,.9); }
.sf-spinner{
  width:22px; height:22px; border:3px solid rgba(255,255,255,.15);
  border-top-color: var(--sf-gold); border-radius:50%;
  animation:sf-spin 1s linear infinite;
}
@keyframes sf-spin{ to{ transform:rotate(360deg); } }

@media (max-width:640px){
  .sf-wallet{ padding:16px 12px; }
  .sf-title{ font-size:1.6rem; }
  .sf-actions{ display:grid; grid-template-columns:1fr 1fr; gap:.6rem; }
}
      `}</style>
    </div>
  );
};

export default WalletPage;
